import { useAuthentication } from '@context/businessContainer';
import {
  fillDataLayer,
  firePageView,
  fireGoogleOptimize,
  extractFacebookPixel,
  extractFacebookClick,
  cleanGtmState,
  dataLayerTrackEvent,
  computeCustomerType,
} from '@utils/tracking';
import { useRouter, Router } from 'next/router';
import { useEffect, useRef } from 'react';
import getConfig from 'next/config';
import { faro } from '@grafana/faro-web-sdk';
import aa from 'search-insights';
import { sendMessageToMobile } from '@utils/mobile';
import { isMobileApp } from '@utils/environment';
import { userService } from '@services/businessContainer';
import useI18n from '@hooks/i18n/useI18n';
import { trackEvent } from '@monofront/tracking';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import { OneTrustCookieCategories } from '@components/Vendors/Vendors';
import getCookieConsent from '@utils/getCookieConsent';

const mqStandAlone = '(display-mode: standalone)';

declare global {
  interface Window {
    google_tag_manager: any;
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

const {
  publicRuntimeConfig: { gtmId, algoliaConfig },
} = getConfig();

const customizedPageViewsPath = ['/home', '/pages/referral', '/cart'];

aa('init', {
  appId: algoliaConfig.app.id,
  apiKey: algoliaConfig.app.key,
});

const TrackingProvider: React.FC = () => {
  const {
    authUser,
    isLoading,
    isMember,
    customer,
    isUserReady,
    isAuthenticated,
  } = useAuthentication();
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);

  const { locale, countryCode } = useI18n();
  const router = useRouter();
  const last = useRef<string | null>(null);

  const clearDataLayer = () => {
    if ('google_tag_manager' in window && window.google_tag_manager[gtmId]) {
      window.google_tag_manager[gtmId].dataLayer.reset();
      cleanGtmState();
    }
  };

  const trackRouteChange = (url: string) => {
    // This is called after the dataLayer been reset:
    // we want to fill customer related values again
    const customerType = computeCustomerType(customer, isMember);

    fillDataLayer(
      {
        connexionStatus: `${authUser ? 'connected' : 'not connected'}`,
        appDisplayMode: window?.matchMedia(mqStandAlone).matches
          ? 'pwa'
          : 'browser',
        customerId: `${authUser?.uid}`,
        customerType,
        languageCode: locale,
        // GA4
        locale,
        countryCode,
        isConnected: isAuthenticated,
        orderCount: customer?.orderCount ?? 0,
        isMaap: isSubscriptionAvailable,
      },
      url,
    );

    sendMessageToMobile('logScreenView', {
      screenName: url,
    });

    if (customer) {
      userService.updateFbPixelData(
        navigator.userAgent,
        extractFacebookPixel(),
        extractFacebookClick(),
        authUser!.uid,
      );
    }
  };

  // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
  const fireTracking = (url: string) => {
    if (
      last.current !== url &&
      !customizedPageViewsPath.includes(url.split('?')[0])
    ) {
      last.current = url;
      firePageView();
      trackEvent({ type: 'LFPageView' });
    }
  };

  // This useEffect replace the dataLayer in the mobile application to send a message
  // to the app each time a component tries to push an event in the dataLayer
  useEffect(() => {
    if (typeof window === 'undefined' || !isMobileApp) {
      return;
    }

    const oldEvents: any[] = [];

    if ('dataLayer' in window) {
      oldEvents.push(...window['dataLayer']);
    } else {
      (window as Window)['dataLayer'] = [];
    }

    window['dataLayer'].push = (data: any) => {
      sendMessageToMobile('dataLayer', {
        url: window.location,
        ...data,
      });

      return Array.prototype.push.apply(window['dataLayer'], data);
    };

    for (const event of oldEvents) {
      window['dataLayer'].push(event);
    }
  }, []);
  // End of mobile app adaptation

  useEffect(() => {
    fireGoogleOptimize();
  }, [router.asPath]);

  // This useEffect update customer related properties when they change
  useEffect(() => {
    if (!isLoading) {
      // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
      const customerType = computeCustomerType(customer, isMember);

      dataLayerTrackEvent('customerType', { customerType });

      fillDataLayer({
        customerId: `${authUser?.uid}`,
        isConnected: isAuthenticated,
        orderCount: customer?.orderCount ?? 0,
        isMaap: isSubscriptionAvailable,
      });
    }
  }, [isLoading, customer, isMember, isAuthenticated]);

  // This useEffect set listener on navigation changes to trigger pageTracking
  useEffect(() => {
    Router.events.on('beforeHistoryChange', trackRouteChange);
    Router.events.on('routeChangeStart', clearDataLayer);
    Router.events.on('routeChangeComplete', fireTracking);

    return () => {
      Router.events.off('beforeHistoryChange', trackRouteChange);
      Router.events.off('routeChangeStart', clearDataLayer);
      Router.events.off('routeChangeComplete', fireTracking);
    };
  }, [isAuthenticated, authUser, customer]);

  // This useEffect trigger the initial pageTracking and add some tracking user context
  useEffect(() => {
    if (isLoading || (authUser && !isUserReady)) {
      return;
    }

    // If the user consents to functionality cookies
    if (authUser && getCookieConsent(OneTrustCookieCategories.FUNCTIONALITY)) {
      aa('setUserToken', authUser.uid);
    }

    trackRouteChange(router.asPath);
    fireTracking(router.asPath);

    // If the user consents to performances cookies
    if (getCookieConsent(OneTrustCookieCategories.ANALYTICS)) {
      // Only the uid + membership flag: telemetry lands in our own Loki, no
      // need to store email/name there.
      faro.api?.setUser({
        id: authUser?.uid,
        attributes: {
          isMember: String(isMember),
        },
      });
    }
  }, [isLoading, authUser?.uid, isUserReady]);

  return null;
};

export { TrackingProvider };
